import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Edit, Trash, Eye, Search, User, Mail, Phone, Building, Trash2, Key, PowerOff, Filter, ArrowUp, ArrowDown, Download, EyeOff, Globe, Building2, Server, CreditCard, X } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import * as XLSX from 'xlsx';
import { customersAPI, companiesAPI } from '../lib/api';
import bcrypt from 'bcryptjs';

const Customers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteRelationsInfo, setDeleteRelationsInfo] = useState(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetCustomer, setPasswordResetCustomer] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [sendPasswordEmail, setSendPasswordEmail] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  // Pasif hale getirme modal state'leri
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingCustomer, setDeactivatingCustomer] = useState(null);
  const [customerRelations, setCustomerRelations] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState({
    companies: [],
    domains: [],
    servers: [],
    payments: []
  });
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    tax_number: '',
    description: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_ids: [],
    address: '',
    notes: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if we came from CustomerDetail with edit state
  useEffect(() => {
    if (location.state?.editCustomer) {
      handleEdit(location.state.editCustomer);
      // Clear the state to prevent re-editing on refresh
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await customersAPI.getAll();
      setCustomers(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      console.error('Müşteriler getirilemedi:', err);
      setError(err.message || t('customers.errorFetch'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companiesAPI.getAll();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Şirketler getirilemedi:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCompanyToggle = (companyId) => {
    setFormData(prev => {
      const currentIds = prev.company_ids || [];
      const newIds = currentIds.includes(companyId)
        ? currentIds.filter(id => id !== companyId)
        : [...currentIds, companyId];
      return {
        ...prev,
        company_ids: newIds
      };
    });
  };

  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company_ids: [],
      address: '',
      notes: '',
      password: ''
    });
    setShowPassword(false);
    setEditingCustomer(null);
    setShowAddForm(false);
  };

  const resetCompanyForm = () => {
    setCompanyFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      tax_number: '',
      description: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Form verilerini hazırla
      const submitData = { ...formData };
      
      // Password varsa hash'le
      if (submitData.password && submitData.password.trim()) {
        const saltRounds = 10;
        submitData.password = await bcrypt.hash(submitData.password.trim(), saltRounds);
      } else {
        // Password yoksa veya boşsa, güncelleme sırasında password alanını kaldır
        delete submitData.password;
      }

      let result;
      if (editingCustomer) {
        // Müşteri güncelle
        result = await customersAPI.update(editingCustomer.id, submitData);
        // Güncellenmiş müşteriyi listeye ekle (şirket bilgileri ile)
        const updatedCustomer = await customersAPI.getById(editingCustomer.id);
        setCustomers(customers.map(customer => 
          customer.id === editingCustomer.id ? updatedCustomer : customer
        ));
      } else {
        // Yeni müşteri oluştur - password zorunlu
        if (!submitData.password) {
          setError(t('customers.errorPasswordRequired'));
          setLoading(false);
          return;
        }
        result = await customersAPI.create(submitData);
        // Oluşturulan müşteriyi listeye ekle (şirket bilgileri ile)
        const newCustomer = await customersAPI.getById(result.id);
        setCustomers([...customers, newCustomer]);
      }
      resetForm();
      setError('');
    } catch (err) {
      console.error('Müşteri işlemi hatası:', err);
      setError(err.message || t('customers.errorOperation'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const newCompany = await companiesAPI.create(companyFormData);
      setCompanies([...companies, newCompany]);
      setFormData(prev => ({ ...prev, company: newCompany.name }));
      setShowCompanyModal(false);
      resetCompanyForm();
      setError('');
    } catch (err) {
      console.error('Şirket ekleme hatası:', err);
      setError(err.message || t('customers.errorCompanyAdd'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    
    // Müşterinin firmalarını al (company_ids veya companies array'inden)
    const companyIds = customer.company_ids || (customer.companies ? customer.companies.map(c => c.id) : []);
    
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone || '',
      company_ids: companyIds,
      address: customer.address || '',
      notes: customer.notes || '',
      password: '' // Güvenlik için password'u göstermiyoruz
    });
    setShowPassword(false);
    setShowAddForm(true);
  };

  const handleDelete = async (customerId) => {
    setDeleteCustomerId(customerId);
    setDeletingBulk(false);
    
    // İlişkileri kontrol et
    try {
      const response = await fetch('/api/customers/check-relations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ids: [customerId] })
      });
      
      const data = await response.json();
      if (response.ok) {
        setDeleteRelationsInfo(data.data);
        setShowDeleteConfirmModal(true);
      }
    } catch (err) {
      console.error('İlişki kontrol hatası:', err);
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    const filteredIds = filteredCustomers.map(customer => customer.id);
    const allFilteredSelected = filteredIds.every(id => selectedCustomers.includes(id));
    
    if (allFilteredSelected) {
      // Filtrelenmiş listedeki seçimleri kaldır
      setSelectedCustomers(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Filtrelenmiş listedeki tümünü seç
      setSelectedCustomers(prev => {
        const newSelection = [...prev];
        filteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCustomers.length === 0) {
      setError(t('customers.errorSelectToDelete'));
      return;
    }

    setDeleteCustomerId(null);
    setDeletingBulk(true);
    
    // İlişkileri kontrol et
    try {
      const response = await fetch('/api/customers/check-relations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ids: selectedCustomers })
      });
      
      const data = await response.json();
      if (response.ok) {
        setDeleteRelationsInfo(data.data);
        setShowDeleteConfirmModal(true);
      }
    } catch (err) {
      console.error('İlişki kontrol hatası:', err);
    }
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('customers.errorSession'));
      return;
    }

    try {
      setLoading(true);
      
      if (deletingBulk) {
        // Toplu silme
        const response = await fetch('/api/customers/bulk-delete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ ids: selectedCustomers })
        });

        const data = await response.json();

        if (response.ok) {
          setCustomers(customers.filter(customer => !selectedCustomers.includes(customer.id)));
          setSelectedCustomers([]);
          setError('');
          setToast({ show: true, message: data.message || `${selectedCustomers.length} müşteri başarıyla silindi`, type: 'success' });
          setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
        } else {
          setError(data.error || 'Toplu silme işlemi başarısız');
        }
      } else {
        // Tekli silme
        await customersAPI.delete(deleteCustomerId);
        setCustomers(customers.filter(customer => customer.id !== deleteCustomerId));
        setError('');
        setToast({ show: true, message: 'Müşteri ve bağlı tüm varlıklar başarıyla silindi', type: 'success' });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
      }
      
      setShowDeleteConfirmModal(false);
      setDeleteRelationsInfo(null);
    } catch (err) {
      console.error('Müşteri silme hatası:', err);
      setError(err.message || t('customers.errorDelete'));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (customer) => {
    navigate(`/customers/${customer.id}`);
  };

  const openCompanyModal = () => {
    setShowCompanyModal(true);
  };

  const handleToggleStatus = async (customerId, currentStatus) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('customers.errorSession'));
      return;
    }

    const newStatus = !currentStatus;

    try {
      const response = await fetch(`/api/customers/${customerId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      const data = await response.json();

      if (response.ok) {
        setCustomers(customers.map(customer => 
          customer.id === customerId ? { ...customer, is_active: newStatus } : customer
        ));
        setError('');
      } else {
        if (response.status === 401 || response.status === 403) {
          setError(t('customers.errorSessionExpired'));
          localStorage.removeItem('token');
        } else {
          setError(data.error || t('customers.statusError'));
        }
      }
    } catch (err) {
      setError(t('customers.errorConnection'));
    }
  };

  const handleBulkStatusUpdate = async (isActive) => {
    if (selectedCustomers.length === 0) {
      setError(t('customers.errorSelectToUpdate'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('customers.errorSession'));
      return;
    }

    const confirmMessage = t(isActive ? 'customers.confirmActivateMultiple' : 'customers.confirmDeactivateMultiple', { count: selectedCustomers.length });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch('/api/customers/bulk-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedCustomers, is_active: isActive })
      });

      const data = await response.json();

      if (response.ok) {
        setCustomers(customers.map(customer => 
          selectedCustomers.includes(customer.id) ? { ...customer, is_active: isActive } : customer
        ));
        setSelectedCustomers([]);
        setError('');
        alert(data.message || t('customers.successUpdated', { count: selectedCustomers.length }));
      } else {
        if (response.status === 401 || response.status === 403) {
          setError(t('customers.errorSessionExpired'));
          localStorage.removeItem('token');
        } else {
          setError(data.error || t('customers.errorOperation'));
        }
      }
    } catch (err) {
      setError(t('customers.errorConnection'));
    }
  };

  const handlePasswordReset = (customer) => {
    setPasswordResetCustomer(customer);
    setNewPassword('');
    setSendPasswordEmail(false);
    setShowPasswordResetModal(true);
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      setError(t('customers.errorPasswordMinLength'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Password'u hash'le
      const saltRounds = 10;
      const hashedPassword = await bcrypt.hash(newPassword.trim(), saltRounds);

      // Müşterinin password'unu güncelle
      await customersAPI.update(passwordResetCustomer.id, {
        password: hashedPassword
      });

      setShowPasswordResetModal(false);
      setPasswordResetCustomer(null);
      setNewPassword('');
      setSendPasswordEmail(false);
      setError('');
      
      // Müşteri listesini yenile
      await fetchCustomers();
      
      // Toast notification göster
      setToast({ show: true, message: t('customers.successPasswordReset'), type: 'success' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Parola sıfırlama hatası:', err);
      setError(err.message || t('customers.errorOperation'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (customer) => {
    setDeactivatingCustomer(customer);
    setLoadingRelations(true);
    setShowDeactivateModal(true);
    
    try {
      const response = await fetch(`/api/customers/${customer.id}/relations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomerRelations(data.data);
        // Varsayılan olarak hepsini seç
        setSelectedEntities({
          companies: data.data.companies.map(c => c.id),
          domains: data.data.domains.map(d => d.id),
          servers: data.data.servers.map(s => s.id),
          payments: data.data.payments.map(p => p.id)
        });
      }
    } catch (err) {
      console.error('İlişkiler yüklenirken hata:', err);
      setError('İlişkili veriler yüklenemedi');
    } finally {
      setLoadingRelations(false);
    }
  };

  const toggleEntitySelection = (type, id) => {
    setSelectedEntities(prev => ({
      ...prev,
      [type]: prev[type].includes(id)
        ? prev[type].filter(itemId => itemId !== id)
        : [...prev[type], id]
    }));
  };

  const toggleAllEntities = (type) => {
    const allIds = customerRelations?.[type]?.map(item => item.id) || [];
    const allSelected = allIds.every(id => selectedEntities[type].includes(id));
    
    setSelectedEntities(prev => ({
      ...prev,
      [type]: allSelected ? [] : allIds
    }));
  };

  const confirmDeactivate = async () => {
    if (!deactivatingCustomer) return;
    
    try {
      setLoading(true);
      const response = await fetch('/api/customers/bulk-deactivate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          customerIds: [deactivatingCustomer.id],
          entityIds: selectedEntities
        })
      });
      
      if (response.ok) {
        setShowDeactivateModal(false);
        setDeactivatingCustomer(null);
        setCustomerRelations(null);
        setSelectedEntities({ companies: [], domains: [], servers: [], payments: [] });
        await fetchCustomers();
        setToast({ show: true, message: 'Müşteri ve seçilen varlıklar pasif hale getirildi', type: 'success' });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Pasif hale getirme başarısız');
      }
    } catch (err) {
      console.error('Pasif hale getirme hatası:', err);
      setError('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ArrowUp size={14} className="inline ml-1" /> : <ArrowDown size={14} className="inline ml-1" />;
  };

  // XLSX Export fonksiyonu
  const handleExport = () => {
    const dataToExport = filteredCustomers.map(customer => ({
      [t('customers.exportColumns.name')]: customer.name || '',
      [t('customers.exportColumns.email')]: customer.email || '',
      [t('customers.exportColumns.phone')]: customer.phone || '',
      [t('customers.exportColumns.address')]: customer.address || '',
      [t('customers.exportColumns.companies')]: customer.company_names || (customer.companies ? customer.companies.map(c => c.name).join(', ') : ''),
      [t('customers.exportColumns.status')]: customer.is_active ? t('customers.statusActive') : t('customers.statusInactive'),
      [t('customers.exportColumns.createdAt')]: customer.created_at ? new Date(customer.created_at).toLocaleDateString('tr-TR') : ''
    }));

    // XLSX oluştur
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('customers.exportSheetName'));
    
    // Dosyayı indir
    XLSX.writeFile(wb, `${t('customers.exportFileName')}${new Date().toISOString().split('T')[0]}.xlsx`);
    
    alert(t('customers.successExport'));
  };

  const filteredCustomers = customers.filter(customer => {
    // Arama filtresi
    const matchesSearch = customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (customer.phone && customer.phone.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (customer.company_names && customer.company_names.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (customer.companies && customer.companies.some(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())));
    
    // Durum filtresi
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && customer.is_active === true) ||
      (statusFilter === 'inactive' && customer.is_active === false);
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue, bValue;
    
    switch (sortColumn) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'email':
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
        break;
      case 'phone':
        aValue = a.phone?.toLowerCase() || '';
        bValue = b.phone?.toLowerCase() || '';
        break;
      case 'company':
        aValue = a.company_names?.toLowerCase() || (a.companies ? a.companies.map(c => c.name).join(', ').toLowerCase() : '');
        bValue = b.company_names?.toLowerCase() || (b.companies ? b.companies.map(c => c.name).join(', ').toLowerCase() : '');
        break;
      case 'address':
        aValue = a.address?.toLowerCase() || '';
        bValue = b.address?.toLowerCase() || '';
        break;
      case 'status':
        aValue = a.is_active ? 1 : 0;
        bValue = b.is_active ? 1 : 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[100] animate-in fade-in slide-in-from-top-2 duration-300">
          <div className={`rounded-lg shadow-lg p-4 flex items-center gap-3 ${
            toast.type === 'success'
              ? 'bg-green-500 text-white'
              : 'bg-red-500 text-white'
          }`}>
            <div className="flex-1">
              {toast.message}
            </div>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="ml-2 text-lg hover:opacity-75"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('customers.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('customers.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Download className="h-5 w-5" />
              <span className="font-medium">Export</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">{t('customers.addNew')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Arama ve Filtreleme */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Arama */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={t('customers.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Durum Filtresi */}
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400" size={20} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">{t('customers.filterAll')}</option>
              <option value="active">{t('customers.filterActive')}</option>
              <option value="inactive">{t('customers.filterInactive')}</option>
            </select>
          </div>
          
          {/* Sonuç Sayısı */}
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <span>{filteredCustomers.length} {t('customers.resultsFound')}</span>
          </div>
        </div>
      </div>

      {/* Hata Mesajı */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Müşteri Ekleme/Düzenleme Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingCustomer ? t('customers.editCustomer') : t('customers.addNew')}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.name')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.email')} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.password')} {!editingCustomer && '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingCustomer}
                    placeholder={editingCustomer ? t('customers.passwordChange') : t('customers.passwordRequired')}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {editingCustomer && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('customers.passwordHint')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.companiesMultiple')}
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-48 overflow-y-auto bg-white dark:bg-gray-700">
                    {companies.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('customers.noCompanies')}</p>
                    ) : (
                      <div className="space-y-2">
                        {companies.map(company => (
                          <label
                            key={company.id}
                            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={formData.company_ids?.includes(company.id) || false}
                              onChange={() => handleCompanyToggle(company.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{company.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={openCompanyModal}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center gap-1 h-fit"
                    title={t('customers.newCompany')}
                  >
                    <Plus size={16} />
                  </button>
                </div>
                {formData.company_ids && formData.company_ids.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.company_ids.length} {t('customers.companiesSelected')}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('customers.address')}
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('customers.notes')}
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t('customers.cancelButton')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {loading ? t('customers.saving') : (editingCustomer ? t('customers.updateButton') : t('customers.saveButton'))}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* Müşteri Listesi - WISECP Style */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {selectedCustomers.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30 px-4 py-2 flex items-center justify-between">
            <span className="text-sm text-blue-700 dark:text-blue-300">
              {selectedCustomers.length} {t('customers.selectedCount')}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkStatusUpdate(true)}
                className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded"
              >
                {t('customers.activateSelected')}
              </button>
              <button
                onClick={() => handleBulkStatusUpdate(false)}
                className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 rounded"
              >
                {t('customers.deactivateSelected')}
              </button>
              <button
                onClick={handleBulkDelete}
                className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded"
              >
                {t('customers.deleteSelected')}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filteredCustomers.length > 0 && filteredCustomers.every(customer => selectedCustomers.includes(customer.id))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('name')}
                >
                  {t('customers.tableCustomer')} {getSortIcon('name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  {t('customers.tableContact')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  {t('customers.tableCompany')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  {t('customers.tableStatus')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-28">
                  {t('customers.tableActions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                    {searchQuery || statusFilter !== 'all'
                      ? t('customers.noCustomersFiltered')
                      : t('customers.noCustomers')}
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={() => handleSelectCustomer(customer.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                       <div
                         className="flex items-center gap-3 cursor-pointer hover:opacity-80"
                         onClick={() => navigate(`/customers/${customer.id}`)}
                       >
                         <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                           <User className="h-5 w-5 text-white" />
                         </div>
                         <div className="min-w-0 flex-1">
                           <div className="text-sm font-semibold text-gray-900 dark:text-white hover:text-blue-600">
                             {customer.name}
                           </div>
                           {customer.notes && (
                             <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                               {customer.notes}
                             </div>
                           )}
                         </div>
                       </div>
                     </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <Mail size={14} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate">{customer.email}</span>
                        </div>
                        {customer.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <Phone size={14} className="text-gray-400 flex-shrink-0" />
                            <span>{customer.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {customer.company_names || (customer.companies && customer.companies.length > 0) ? (
                        <div className="space-y-1">
                          {(customer.companies || []).slice(0, 2).map((company, idx) => (
                            <div key={company.id || idx} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Building size={14} className="text-gray-400 flex-shrink-0" />
                              <span className="truncate">{company.name}</span>
                            </div>
                          ))}
                          {customer.companies && customer.companies.length > 2 && (
                            <span className="text-xs text-gray-500">+{customer.companies.length - 2} {t('customers.more')}</span>
                          )}
                          {customer.company_names && !customer.companies && (
                            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Building size={14} className="text-gray-400 flex-shrink-0" />
                              <span className="truncate">{customer.company_names}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(customer.id, customer.is_active === true)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                          customer.is_active === true
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200'
                        }`}
                      >
                        {customer.is_active === true ? (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            {t('customers.statusActive')}
                          </>
                        ) : (
                          <>
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                            {t('customers.statusInactive')}
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleView(customer)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title={t('customers.view')}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(customer)}
                          className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                          title={t('customers.edit')}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handlePasswordReset(customer)}
                          className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                          title={t('customers.resetPassword')}
                        >
                          <Key size={16} />
                        </button>
                        {customer.is_active === true && (
                          <button
                            onClick={() => handleDeactivate(customer)}
                            className="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                            title="Pasif Hale Getir"
                          >
                            <PowerOff size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(customer.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title={t('customers.delete')}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Tablo Alt Bilgisi */}
        <div className="bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>
            {filteredCustomers.length} {t('customers.resultsFound')}
          </span>
          <div className="flex items-center gap-2">
            <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50" disabled>
              {t('customers.previous')}
            </button>
            <span className="px-2 py-1 bg-blue-600 text-white rounded">1</span>
            <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50" disabled>
              {t('customers.next')}
            </button>
          </div>
        </div>
      </div>

      {/* Şirket Ekleme Modal */}
      {showCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('customers.newCompanyTitle')}</h3>
              <button
                onClick={() => setShowCompanyModal(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCompanySubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customers.companyName')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={companyFormData.name}
                    onChange={handleCompanyInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customers.companyPhone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={companyFormData.phone}
                    onChange={handleCompanyInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customers.companyEmail')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={companyFormData.email}
                    onChange={handleCompanyInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customers.companyWebsite')}
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={companyFormData.website}
                    onChange={handleCompanyInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customers.companyTaxNumber')}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={companyFormData.tax_number}
                    onChange={handleCompanyInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.companyAddress')}
                </label>
                <textarea
                  name="address"
                  value={companyFormData.address}
                  onChange={handleCompanyInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.companyDescription')}
                </label>
                <textarea
                  name="description"
                  value={companyFormData.description}
                  onChange={handleCompanyInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                >
                  {loading ? t('customers.saving') : t('customers.companyAddButton')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCompanyModal(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
                >
                  {t('customers.cancelButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteConfirmModal && deleteRelationsInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Trash2 size={20} className="text-red-500" />
                {deletingBulk ? 'Toplu Müşteri Silme Onayı' : 'Müşteri Silme Onayı'}
              </h3>
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setDeleteRelationsInfo(null);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Uyarı Mesajı */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                  ⚠️ Bu işlem geri alınamaz!
                </p>
              </div>

              {/* Silinecek Müşteriler */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {deletingBulk ? 'Silinecek müşteriler:' : 'Silinecek müşteri:'}
                </p>
                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {deletingBulk ? (
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {deleteRelationsInfo.customerNames?.slice(0, 10).map((name, idx) => (
                        <li key={idx}>• {name}</li>
                      ))}
                      {deleteRelationsInfo.customerCount > 10 && (
                        <li className="text-gray-500">...ve {deleteRelationsInfo.customerCount - 10} müşteri daha</li>
                      )}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {deleteRelationsInfo.customerNames?.[0] || '-'}
                    </p>
                  )}
                </div>
              </div>

              {/* Silinecek Bağlı Varlıklar */}
              {(deleteRelationsInfo.domainsCount > 0 || deleteRelationsInfo.paymentsCount > 0) && (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Aşağıdaki bağlı varlıklar da silinecek:
                  </p>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-2">
                    {deleteRelationsInfo.domainsCount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-red-700 dark:text-red-300">🌐 Domainler</span>
                        <span className="font-semibold text-red-700 dark:text-red-300">{deleteRelationsInfo.domainsCount} adet</span>
                      </div>
                    )}
                    {deleteRelationsInfo.paymentsCount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-red-700 dark:text-red-300">💳 Ödemeler</span>
                        <span className="font-semibold text-red-700 dark:text-red-300">{deleteRelationsInfo.paymentsCount} adet</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Firmalar Silinmeyecek Bilgisi */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-sm text-green-700 dark:text-green-300">
                  ✓ Firmalar silinmeyecek, sadece müşteri-firma ilişkileri kaldırılacak
                </p>
              </div>

              {/* Butonlar */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowDeleteConfirmModal(false);
                    setDeleteRelationsInfo(null);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                >
                  İptal
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      {deletingBulk ? 'Toplu Sil' : 'Sil'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parola Resetleme Modal */}
      {showPasswordResetModal && passwordResetCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('customers.resetPasswordTitle')}</h3>
              <button
                onClick={() => {
                  setShowPasswordResetModal(false);
                  setPasswordResetCustomer(null);
                  setNewPassword('');
                  setSendPasswordEmail(false);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <strong>{passwordResetCustomer.name}</strong> {t('customers.resetPasswordDesc')}
              </p>
            </div>
            <form onSubmit={handlePasswordResetSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customers.newPassword')} *
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={t('customers.newPasswordPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">{t('customers.newPasswordHint')}</p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={sendPasswordEmail}
                  onChange={(e) => setSendPasswordEmail(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="sendEmail" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('customers.sendEmail')}
                </label>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setPasswordResetCustomer(null);
                    setNewPassword('');
                    setSendPasswordEmail(false);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                >
                  {t('customers.cancelButton')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md"
                >
                  {t('customers.resetButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pasif Hale Getirme Modal */}
      {showDeactivateModal && deactivatingCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <PowerOff size={20} className="text-orange-500" />
                Pasif Hale Getir - {deactivatingCustomer.name}
              </h3>
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setDeactivatingCustomer(null);
                  setCustomerRelations(null);
                  setSelectedEntities({ companies: [], domains: [], servers: [], payments: [] });
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>

            {loadingRelations ? (
              <div className="p-12 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
              </div>
            ) : customerRelations ? (
              <div className="p-6 space-y-6">
                {/* Uyarı Mesajı */}
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                    ⚠️ Bu işlem müşteriyi ve seçili varlıkları pasif hale getirecektir.
                  </p>
                </div>

                {/* Şirketler */}
                {customerRelations.companies.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Building2 size={18} className="text-blue-500" />
                        Şirketler ({customerRelations.companies.length})
                      </h4>
                      <button
                        onClick={() => toggleAllEntities('companies')}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {selectedEntities.companies.length === customerRelations.companies.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {customerRelations.companies.map(company => (
                        <label
                          key={company.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            selectedEntities.companies.includes(company.id)
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'bg-gray-50 dark:bg-gray-700/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedEntities.companies.includes(company.id)}
                            onChange={() => toggleEntitySelection('companies', company.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{company.name}</span>
                          {!company.is_active && <span className="text-xs text-red-500">(Zaten Pasif)</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Domainler */}
                {customerRelations.domains.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Globe size={18} className="text-purple-500" />
                        Domainler ({customerRelations.domains.length})
                      </h4>
                      <button
                        onClick={() => toggleAllEntities('domains')}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {selectedEntities.domains.length === customerRelations.domains.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {customerRelations.domains.map(domain => (
                        <label
                          key={domain.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            selectedEntities.domains.includes(domain.id)
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'bg-gray-50 dark:bg-gray-700/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedEntities.domains.includes(domain.id)}
                            onChange={() => toggleEntitySelection('domains', domain.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {domain.domain_name}.{domain.extension}
                          </span>
                          {!domain.is_active && <span className="text-xs text-red-500">(Zaten Pasif)</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Serverlar */}
                {customerRelations.servers.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Server size={18} className="text-green-500" />
                        Sunucular ({customerRelations.servers.length})
                      </h4>
                      <button
                        onClick={() => toggleAllEntities('servers')}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {selectedEntities.servers.length === customerRelations.servers.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {customerRelations.servers.map(server => (
                        <label
                          key={server.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            selectedEntities.servers.includes(server.id)
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'bg-gray-50 dark:bg-gray-700/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedEntities.servers.includes(server.id)}
                            onChange={() => toggleEntitySelection('servers', server.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{server.name}</span>
                          {!server.is_active && <span className="text-xs text-red-500">(Zaten Pasif)</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Siparişler / Tekrarlı Ödemeler */}
                {customerRelations.payments.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <CreditCard size={18} className="text-yellow-500" />
                        Siparişler / Tekrarlı Ödemeler ({customerRelations.payments.length})
                      </h4>
                      <button
                        onClick={() => toggleAllEntities('payments')}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {selectedEntities.payments.length === customerRelations.payments.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {customerRelations.payments.map(payment => (
                        <label
                          key={payment.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            selectedEntities.payments.includes(payment.id)
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'bg-gray-50 dark:bg-gray-700/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedEntities.payments.includes(payment.id)}
                            onChange={() => toggleEntitySelection('payments', payment.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{payment.package_name || 'Sipariş'}</span>
                          {!payment.is_active && <span className="text-xs text-red-500">(Zaten Pasif)</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hiç ilişkili veri yoksa */}
                {customerRelations.companies.length === 0 &&
                 customerRelations.domains.length === 0 &&
                 customerRelations.servers.length === 0 &&
                 customerRelations.payments.length === 0 && (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Bu müşterinin ilişkili verisi bulunmuyor.</p>
                  </div>
                )}
              </div>
            ) : null}

            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setDeactivatingCustomer(null);
                  setCustomerRelations(null);
                  setSelectedEntities({ companies: [], domains: [], servers: [], payments: [] });
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
              >
                İptal
              </button>
              <button
                onClick={confirmDeactivate}
                disabled={loading}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <PowerOff size={16} />
                    Pasif Hale Getir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
